import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { usePayoutsList } from '../../../hooks/usePayoutsList';
import { useCashbackList } from '../../../hooks/useCashbackList';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import { fmtNum, fmtUsd } from '../../../lib/formatHelpers';

/**
 * Admin V2 Finance — Dashboard finance V2.
 * Route : /admin/finance (V2Switcher) + /admin/v2/finance.
 * Feature : pages.finance.dashboard.
 *
 * Affiche :
 *  - Hero : KPIs synthétiques (total payouts MTD, total cashback MTD, créateurs payés)
 *  - Section "Recent payouts" (3 derniers groupes via usePayoutsList)
 *  - Section "Recent cashback" (3 derniers groupes via useCashbackList)
 *  - Lien vers /admin/manager-payments et /admin/cashback pour le détail
 *
 * Tout chiffré AES — décryptage server-side via Edge Functions Sprint 5.
 */
const Stat = ({ label, value, sub }) => (
  <div className="rounded-xl bg-card border border-border p-4">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">{label}</div>
    <div className="text-2xl md:text-3xl font-heading font-bold text-foreground tabular-nums mt-1">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const AdminV2Finance = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const [collapsed, setCollapsed] = useState(false);

  const payouts = usePayoutsList({});
  const cashback = useCashbackList({});

  const loading = payouts.loading || cashback.loading;
  const decryptionDown = payouts.decryptionAvailable === false || cashback.decryptionAvailable === false;

  // Aggregate top-level KPIs across all payouts + cashback
  const summary = useMemo(() => {
    const totalPayouts = payouts.rows.reduce((s, r) => s + (r.totalAmount ?? 0), 0);
    const totalCommissions = payouts.rows.reduce((s, r) => s + (r.totalCommission ?? 0), 0);
    const totalCashback = cashback.rows.reduce((s, r) => s + (r.totalAmount ?? 0), 0);
    const payoutPeriods = payouts.rows.length;
    const cashbackPeriods = cashback.rows.length;
    const hasPayoutValues = payouts.rows.some((r) => r.totalAmount != null);
    const hasCashbackValues = cashback.rows.some((r) => r.totalAmount != null);
    return {
      totalPayouts: hasPayoutValues ? totalPayouts : null,
      totalCommissions: payouts.rows.some((r) => r.totalCommission != null) ? totalCommissions : null,
      totalCashback: hasCashbackValues ? totalCashback : null,
      payoutPeriods,
      cashbackPeriods,
    };
  }, [payouts.rows, cashback.rows]);

  const recentPayouts = payouts.rows.slice(0, 3);
  const recentCashback = cashback.rows.slice(0, 3);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
              {t('financeV2.title', 'Finance')}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">{tenantName}</p>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {decryptionDown && (
            <div className="rounded-2xl bg-accent/5 border border-accent/20 p-4 text-xs text-muted-foreground">
              <span className="font-semibold text-accent mr-1">{t('paymentsV2.aesNoteLabel', 'AES heads-up:')}</span>
              {t(
                'financeV2.aesNote',
                'Some amounts could not be decrypted. Check the generic-decrypt-* Edge Functions and ENCRYPTION_KEY_GENERIC.'
              )}
            </div>
          )}

          {/* Hero KPIs */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <Stat
              label={t('financeV2.totalPayouts', 'Total payouts')}
              value={loading ? '…' : (summary.totalPayouts != null ? fmtUsd(summary.totalPayouts, { decimals: 2 }) : '—')}
              sub={`${summary.payoutPeriods} ${t('paymentsV2.periods', 'periods')}`}
            />
            <Stat
              label={t('financeV2.totalCommissions', 'Total commissions')}
              value={loading ? '…' : (summary.totalCommissions != null ? fmtUsd(summary.totalCommissions, { decimals: 2 }) : '—')}
            />
            <Stat
              label={t('financeV2.totalCashback', 'Total cashback')}
              value={loading ? '…' : (summary.totalCashback != null ? fmtUsd(summary.totalCashback, { decimals: 2 }) : '—')}
              sub={`${summary.cashbackPeriods} ${t('paymentsV2.periods', 'periods')}`}
            />
            <Stat
              label={t('financeV2.payoutPeriods', 'Payout periods')}
              value={loading ? '…' : fmtNum(summary.payoutPeriods)}
            />
          </section>

          {/* Recent payouts */}
          <section className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-surface-2 flex items-center justify-between">
              <div className="font-heading font-semibold text-foreground text-sm">
                {t('financeV2.recentPayouts', 'Recent payouts')}
              </div>
              <Link to="/admin/manager-payments" className="text-xs text-primary hover:underline">
                {t('financeV2.seeAll', 'See all')} →
              </Link>
            </div>
            {loading ? (
              <div className="h-24 skeleton" />
            ) : recentPayouts.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">{t('financeV2.noPayouts', 'No payouts yet.')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {recentPayouts.map((p) => (
                  <li key={p.key} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground truncate">
                        {t(`months.${p.period_month - 1}`, String(p.period_month))} {p.period_year}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {p.agent_name || t('paymentsV2.unassigned', 'No manager assigned')} · {p.creator_count} {t('paymentsV2.creators', 'creators')}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-heading font-semibold tabular-nums">
                        {p.totalAmount != null ? fmtUsd(p.totalAmount, { decimals: 2 }) : '—'}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Recent cashback */}
          <section className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-surface-2 flex items-center justify-between">
              <div className="font-heading font-semibold text-foreground text-sm">
                {t('financeV2.recentCashback', 'Recent cashback')}
              </div>
              <Link to="/admin/cashback" className="text-xs text-primary hover:underline">
                {t('financeV2.seeAll', 'See all')} →
              </Link>
            </div>
            {loading ? (
              <div className="h-24 skeleton" />
            ) : recentCashback.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">{t('financeV2.noCashback', 'No cashback yet.')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {recentCashback.map((c) => (
                  <li key={c.key} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground truncate">{c.period || '—'}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.count} {t('paymentsV2.creators', 'creators')} · {c.status}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-heading font-semibold tabular-nums">
                        {c.totalAmount != null ? fmtUsd(c.totalAmount, { decimals: 2 }) : '—'}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default AdminV2Finance;
